/**
 * @sazeo/sdk — Orientation Wizard hooks
 *
 * TanStack Query wrappers for the orientation wizard module.
 * All calls route through the Edge Function at /orientation/*.
 * No service_role key — user JWT only.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { buildFunctionUrl } from './functionUrl';
import type { SupabaseClientLike } from './people';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface OrientationTemplate {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  is_default: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface OrientationChecklistItem {
  id: string;
  tenant_id: string;
  template_id: string;
  title: string;
  description: string | null;
  sequence_order: number;
  due_days_after_start: number;
  created_at: string;
}

export interface OrientationProgress {
  id: string;
  employee_id: string;
  template_id: string | null;
  checklist_item_id: string;
  completed_at: string | null;
  completed_by: string | null;
  notes: string | null;
  created_at: string;
}

export interface OrientationTemplateCreateInput {
  tenant_id: string;
  name: string;
  description?: string | null;
  is_default?: boolean;
}

export interface OrientationItemCreateInput {
  tenant_id: string;
  title: string;
  description?: string | null;
  sequence_order?: number;
  due_days_after_start?: number;
}

export interface OrientationStartInput {
  tenant_id: string;
  employee_id: string;
  template_id: string;
}

export interface OrientationCompleteInput {
  progressId: string;
  notes?: string | null;
}

export interface ReorderItem {
  id: string;
  sequence_order: number;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const orientationKeys = {
  all: ['orientation'] as const,
  templates: (tenantId: string) => [...orientationKeys.all, 'templates', tenantId] as const,
  items: (templateId: string) => [...orientationKeys.all, 'items', templateId] as const,
  progress: (employeeId: string) => [...orientationKeys.all, 'progress', employeeId] as const,
} as const;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

async function getAuthHeaders(supabase: SupabaseClientLike): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(
  supabase: SupabaseClientLike,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const headers = await getAuthHeaders(supabase);
  const url = buildFunctionUrl(`orientation${path}`);
  const res = await fetch(url, {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...(init?.headers ?? {}) },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as {
      error?: { message?: string };
    } | null;
    throw new Error(body?.error?.message ?? res.statusText);
  }
  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Hooks — Templates
// ---------------------------------------------------------------------------

/**
 * List all orientation templates for the tenant.
 * Accessible by all tenant members (RLS restricts to own tenant).
 */
export function useOrientationTemplates(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: orientationKeys.templates(tenantId),
    queryFn: () =>
      apiFetch<{ data: OrientationTemplate[] }>(supabase, '/templates').then((r) => r.data),
    enabled: Boolean(tenantId),
  });
}

/**
 * Create a new orientation template.
 * Requires admin/hr_manager role (enforced by RLS).
 */
export function useCreateOrientationTemplate(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: OrientationTemplateCreateInput) =>
      apiFetch<{ data: OrientationTemplate }>(supabase, '/templates', {
        method: 'POST',
        body: JSON.stringify(input),
      }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: orientationKeys.templates(tenantId) }),
  });
}

/**
 * Update an orientation template.
 * Requires admin/hr_manager role.
 */
export function useUpdateOrientationTemplate(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: Partial<OrientationTemplateCreateInput> & { id: string }) =>
      apiFetch<{ data: OrientationTemplate }>(supabase, `/templates/${id}`, {
        method: 'PUT',
        body: JSON.stringify(patch),
      }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: orientationKeys.templates(tenantId) }),
  });
}

/**
 * Delete an orientation template.
 * Requires admin/hr_manager role.
 */
export function useDeleteOrientationTemplate(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ ok: boolean }>(supabase, `/templates/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: orientationKeys.templates(tenantId) }),
  });
}

// ---------------------------------------------------------------------------
// Hooks — Checklist Items
// ---------------------------------------------------------------------------

/**
 * List all checklist items for a template in sequence order.
 */
export function useOrientationItems(supabase: SupabaseClientLike, templateId: string) {
  return useQuery({
    queryKey: orientationKeys.items(templateId),
    queryFn: () =>
      apiFetch<{ data: OrientationChecklistItem[] }>(
        supabase,
        `/templates/${templateId}/items`,
      ).then((r) => r.data),
    enabled: Boolean(templateId),
  });
}

/**
 * Add a checklist item to a template.
 */
export function useAddOrientationItem(supabase: SupabaseClientLike, templateId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: OrientationItemCreateInput) =>
      apiFetch<{ data: OrientationChecklistItem }>(supabase, `/templates/${templateId}/items`, {
        method: 'POST',
        body: JSON.stringify(input),
      }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: orientationKeys.items(templateId) }),
  });
}

/**
 * Delete a checklist item from a template.
 */
export function useDeleteOrientationItem(supabase: SupabaseClientLike, templateId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) =>
      apiFetch<{ ok: boolean }>(supabase, `/templates/${templateId}/items/${itemId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: orientationKeys.items(templateId) }),
  });
}

/**
 * Reorder checklist items (drag-and-drop).
 * Accepts the full reordered array; computes sequence_order from array index.
 */
export function useReorderOrientationItems(supabase: SupabaseClientLike, templateId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items: ReorderItem[]) =>
      apiFetch<{ ok: boolean; updated: number }>(supabase, `/templates/${templateId}/reorder`, {
        method: 'POST',
        body: JSON.stringify({ items }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: orientationKeys.items(templateId) }),
  });
}

// ---------------------------------------------------------------------------
// Hooks — Progress
// ---------------------------------------------------------------------------

/**
 * Start orientation for an employee: creates progress rows for all template items.
 * Requires admin/hr_manager role.
 */
export function useStartOrientation(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: OrientationStartInput) =>
      apiFetch<{ data: OrientationProgress[]; started: number }>(supabase, '/start', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: (_, vars) =>
      qc.invalidateQueries({ queryKey: orientationKeys.progress(vars.employee_id) }),
  });
}

/**
 * Fetch orientation progress for an employee.
 * Employees see their own; admins/hr_manager see any within tenant.
 */
export function useOrientationProgress(supabase: SupabaseClientLike, employeeId: string) {
  return useQuery({
    queryKey: orientationKeys.progress(employeeId),
    queryFn: () =>
      apiFetch<{ data: OrientationProgress[] }>(supabase, `/progress/${employeeId}`).then(
        (r) => r.data,
      ),
    enabled: Boolean(employeeId),
  });
}

/**
 * Mark a single progress item as complete.
 * An employee can mark their own items; admins can mark any item.
 */
export function useCompleteOrientationItem(supabase: SupabaseClientLike, employeeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ progressId, notes }: OrientationCompleteInput) =>
      apiFetch<{ data: OrientationProgress }>(supabase, `/progress/${progressId}/complete`, {
        method: 'POST',
        body: JSON.stringify({ notes: notes ?? null }),
      }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: orientationKeys.progress(employeeId) }),
  });
}

// Alias to match spec name
export const useMarkOrientationItemComplete = useCompleteOrientationItem;
export const useMyOrientation = useOrientationProgress;
